import React, { useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Container,
  Stack,
  Typography,
} from "../ui";
import { brand } from "../config/brand";
import { useOrganization } from "../hooks/useOrganization";
import { chosenOrganization } from "../utils/organizationScope";

/* The colour the app takes on once this organization is chosen - its own
   primary, else the shell's - so each card previews what picking it does. */
const primaryOf = (organization) =>
  organization.palette?.primary ?? brand.palette.primary;

/**
 * The first-run chooser: shown in place of the app until someone has chosen an
 * organization in this browser (docs/changes/organization-scoping/SPEC.md 3d).
 * After that the app-bar switch is how the choice changes.
 *
 * `onChosen` is how RequireOrganization moves on. The hook alone would not:
 * choosing the organization that was already the default stores it but leaves
 * the id unchanged, so nothing subscribed to the id re-renders.
 */
export default function ChooseOrganization({ onChosen }) {
  // Only the ones this user may use (organization-scoping 3.6b).
  const { available: organizations, select } = useOrganization();

  const choose = (id) => {
    select(id);
    onChosen?.(id);
  };

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      {/* No shell logo here: the portal is both companies', and each card
          below carries its own. */}
      <Stack alignItems="center" spacing={1} sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" align="center">
          {brand.name}
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center">
          Choose the organization to work in. You can switch at any time from
          the top bar.
        </Typography>
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={3}
        justifyContent="center"
      >
        {organizations.map((organization) => (
          <Card
            key={organization.id}
            elevation={3}
            sx={{
              flex: 1,
              maxWidth: { sm: 360 },
              borderRadius: 2,
              bgcolor: primaryOf(organization),
              color: "common.white",
              transition: "transform .15s, box-shadow .15s",
              "&:hover": { transform: "translateY(-2px)", boxShadow: 8 },
            }}
          >
            <CardActionArea
              onClick={() => choose(organization.id)}
              data-organization={organization.id}
              sx={{ p: 3 }}
            >
              <Box
                sx={{
                  height: 96,
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* Each logo is drawn in its organization's colour, which is
                    the card's, so it is knocked out to white as on the bar. */}
                <Box
                  component="img"
                  src={organization.logo}
                  alt={organization.logoAlt}
                  sx={{
                    maxHeight: 88,
                    maxWidth: "85%",
                    filter: "brightness(0) invert(1)",
                  }}
                />
              </Box>
              <CardContent sx={{ textAlign: "center", p: 0 }}>
                <Typography variant="h6" component="h2">
                  Sign into {organization.name}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Stack>
    </Container>
  );
}

/**
 * In place of the app for a user who may use no organization: every row an
 * Admin left them names one since removed from config. Every request would be
 * refused, so there is nothing to show but who can fix it.
 */
export function NoOrganization() {
  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Typography variant="h5" component="h1" gutterBottom>
        No organization to work in
      </Typography>
      <Typography variant="body1" color="text.secondary">
        Your account has not been given access to any organization. Ask an Admin
        to choose one for you in User Management, then reload this page.
      </Typography>
    </Container>
  );
}

/**
 * Renders the chooser in place of `children` until an organization has been
 * chosen in this browser. With one organization there is nothing to choose and
 * the chooser never shows, which is the app as it was (SPEC success criterion
 * 6) - and that counts the ones this user may use, so a user an Admin allowed
 * one organization is put in it silently (organization-scoping 3.6b).
 */
export function RequireOrganization({ children }) {
  const { available } = useOrganization();
  const [chosenNow, setChosenNow] = useState(false);

  if (available.length === 0) return <NoOrganization />;
  if (available.length > 1 && !chosenNow && !chosenOrganization()) {
    return <ChooseOrganization onChosen={() => setChosenNow(true)} />;
  }
  return children;
}
